import type { PayloadAction } from '@reduxjs/toolkit';
import { ClaimantCreationResponse } from '@/domains/claimants/types/api/responses/ClaimantCreationResponse';
import { HttpServiceResponse } from '@/services/HttpService/types';
import { RootState } from '@/store/types/RootState';
import { CreateClaimantAutomaticallyRequest } from '../types/api/requests/CreateClaimantAutomaticallyRequest';
import { CreateClaimantManuallyRequest } from '../types/api/requests/CreateClaimantManuallyRequest';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { BeneficiaryDrawerParams } from '@/features/drawer/components/BeneficiaryDrawer/enums/BeneficiaryDrawerParams';
import { Drawers } from '@/features/drawer/enums/Drawers';
import { DrawerService } from '@/features/drawer/services/DrawerService/DrawerService';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import { EdsFilterUtilsService } from '@/features/eds/services/EdsFilterUtilsService/EdsFilterUtilsService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import { getClaimSetClaimListData } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import { FAILED_TO_SAVE } from '@/constants/texts';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import NavigationService from '@/services/NavigationService/NavigationService';
import SnacksService from '@/services/SnackService/SnackService';
import { checkContactInformation } from '@/utils/checkContactInformation';
import { defineCreateClaimantSuccessMessage } from '../constants/defineCreateClaimantSuccessMessage';
import { getClaimantsList } from '../store/claimantsListSlice';
import {
  createClaimantAutomatically,
  createClaimantManually,
  createClaimantSuccess,
  resetCreateClaimantState,
  setCreateClaimantLoadingStatus,
} from '../store/createClaimantSlice';

function* createClaimantManuallySaga({
  payload: { body, relationshipType, searchParam },
}: PayloadAction<CreateClaimantManuallyRequest>) {
  try {
    yield put(setCreateClaimantLoadingStatus(true));
    const claimSetId = yield select(selectCurrentClaimSetId);

    const requestSearchParam = searchParam ? `?${searchParam}=true` : '';
    const { data }: HttpServiceResponse<ClaimantCreationResponse> = yield call(
      HttpService.post,
      `/examiner/claimants/create-${relationshipType}-claimant-manually${requestSearchParam}`,
      { ...body, contactInformation: checkContactInformation(body.contactInformation) },
    );

    yield put(getClaimSetClaimListData({ claimSetId: String(claimSetId) }));

    const claimantsListFilters = EdsFilterUtilsService.prepareCustomFiltersForSending({
      [EdsFilterEntities.ClaimId]: [`${body.claimId}`],
    });
    yield put(getClaimantsList({ filters: claimantsListFilters }));
    yield put(createClaimantSuccess());

    SnacksService.add({
      type: SnackType.Success,
      title: defineCreateClaimantSuccessMessage(
        body.business?.businessName ?? `${body.person?.firstName} ${body.person?.lastName}`,
      ),
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    ModalService.close(Modals.AddClaimant);

    yield delay(300);

    // TODO Replace it with the correct response from the backend in the request above with the entire entity instance
    const { data: claimantEntity } = yield call(
      HttpService.get,
      `/v2/${EdsEntityCodes.Claimant}/${data.id}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    NavigationService.navigateTo(
      [
        location.pathname,
        DrawerService.prepareSearchParams(Drawers.Beneficiary, {
          [BeneficiaryDrawerParams.ClaimId]: body.claimId,
          [BeneficiaryDrawerParams.BeneficiaryId]: claimantEntity.beneficiaryId,
          [BeneficiaryDrawerParams.Expanded]: true,
        }),
      ].join('?'),
      { replace: true },
    );
  } catch (error) {
    SnacksService.add({ type: SnackType.Error, title: FAILED_TO_SAVE });
    ModalService.close(Modals.AddClaimant);
  } finally {
    yield put(setCreateClaimantLoadingStatus(false));
    yield put(resetCreateClaimantState());
  }
}

function* createClaimantAutomaticallySaga({ payload }: PayloadAction<CreateClaimantAutomaticallyRequest>) {
  try {
    yield put(setCreateClaimantLoadingStatus(true));
    const claimSetId = yield select(selectCurrentClaimSetId);

    const { data }: HttpServiceResponse<ClaimantCreationResponse> = yield call(
      HttpService.post,
      `/examiner/claimants/create-claimant-automatically`,
      payload,
    );

    yield put(getClaimSetClaimListData({ claimSetId: String(claimSetId) }));

    const claimantsListFilters = EdsFilterUtilsService.prepareCustomFiltersForSending({
      [EdsFilterEntities.ClaimId]: [`${payload.claimId}`],
    });
    yield put(getClaimantsList({ filters: claimantsListFilters }));
    yield put(createClaimantSuccess());

    const userForClaimantCreate = yield select((state: RootState) => state.createClaimant.userForClaimantCreate);
    SnacksService.add({
      type: SnackType.Success,
      title: defineCreateClaimantSuccessMessage(userForClaimantCreate?.name ?? ''),
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    ModalService.close(Modals.AddClaimant);

    yield delay(300);

    // TODO Replace it with the correct response from the backend in the request above with the entire entity instance
    const { data: claimantEntity } = yield call(
      HttpService.get,
      `/v2/${EdsEntityCodes.Claimant}/${data.id}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    NavigationService.navigateTo(
      [
        location.pathname,
        DrawerService.prepareSearchParams(Drawers.Beneficiary, {
          [BeneficiaryDrawerParams.ClaimId]: payload.claimId,
          [BeneficiaryDrawerParams.BeneficiaryId]: claimantEntity.beneficiaryId,
          [BeneficiaryDrawerParams.Expanded]: true,
        }),
      ].join('?'),
      { replace: true },
    );
  } catch (error) {
    SnacksService.add({ type: SnackType.Error, title: FAILED_TO_SAVE });
    ModalService.close(Modals.AddClaimant);
  } finally {
    yield put(setCreateClaimantLoadingStatus(false));
    yield put(resetCreateClaimantState());
  }
}

export function* createClaimantWatcher() {
  yield takeLatest(createClaimantManually.type, createClaimantManuallySaga);
  yield takeLatest(createClaimantAutomatically.type, createClaimantAutomaticallySaga);
}
